import React from 'react';
import { Paper, Stack, ToggleButton, ToggleButtonGroup, Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import DateInput from '../inputs/DateInput';

const PRESETS = [
    { key: '7d', labelEn: 'Last 7 days', labelAr: 'آخر 7 أيام', days: 7 },
    { key: '30d', labelEn: 'Last 30 days', labelAr: 'آخر 30 يوم', days: 30 },
    { key: '90d', labelEn: 'Last 90 days', labelAr: 'آخر 90 يوم', days: 90 },
    { key: '12m', labelEn: 'Last 12 months', labelAr: 'آخر 12 شهر', days: 365 },
];

export const buildDefaultRange = () => ({
    from: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
    granularity: 'day',
});

const ReportsFilterBar = ({ value, onChange }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const handlePreset = (days) => {
        const granularity = days >= 365 ? 'month' : days >= 90 ? 'week' : 'day';
        onChange({
            from: dayjs().subtract(days - 1, 'day').format('YYYY-MM-DD'),
            to: dayjs().format('YYYY-MM-DD'),
            granularity,
        });
    };

    const handleDate = (field) => (event) => {
        onChange({ ...value, [field]: event.target.value });
    };

    const handleGranularity = (_e, val) => {
        if (val) onChange({ ...value, granularity: val });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'flex-end' }}
                flexWrap="wrap"
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {isArabic ? 'نطاقات سريعة' : 'Quick ranges'}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {PRESETS.map((p) => (
                            <Button
                                key={p.key}
                                size="small"
                                variant="outlined"
                                onClick={() => handlePreset(p.days)}
                            >
                                {isArabic ? p.labelAr : p.labelEn}
                            </Button>
                        ))}
                    </Stack>
                </Box>

                <Box sx={{ minWidth: 160 }}>
                    <DateInput
                        label={isArabic ? 'من' : 'From'}
                        value={value.from}
                        onChange={handleDate('from')}
                        max={value.to}
                    />
                </Box>
                <Box sx={{ minWidth: 160 }}>
                    <DateInput
                        label={isArabic ? 'إلى' : 'To'}
                        value={value.to}
                        onChange={handleDate('to')}
                        min={value.from}
                    />
                </Box>

                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {isArabic ? 'الدقة الزمنية' : 'Granularity'}
                    </Typography>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={value.granularity}
                        onChange={handleGranularity}
                    >
                        <ToggleButton value="day">{isArabic ? 'يومي' : 'Day'}</ToggleButton>
                        <ToggleButton value="week">{isArabic ? 'أسبوعي' : 'Week'}</ToggleButton>
                        <ToggleButton value="month">{isArabic ? 'شهري' : 'Month'}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Stack>
        </Paper>
    );
};

export default ReportsFilterBar;
